import React from 'react';

import i18n from '@client/utils/i18n';
import lediumApi from '@client/utils/lediumApi';
import type { DmxResolution, LinkedZoneDoc } from '@ledium/types';
import { Box, Dialog, DialogContent, DialogTitle, Skeleton, Typography } from '@mui/material';

import LinkedProjectRow from './LinkedProjectRow';

interface UsedLightChannel {
    id: string;
    name: string;
    dmxAddress: number;
    resolution: DmxResolution;
}

interface ManageLinksDialogProps {
    open: boolean;
    zoneId: string;
    title: string;
    onClose: () => void;
    onEditLink?: (link: LinkedZoneDoc) => void;
    refreshKey?: number;
}

function ManageLinksDialog({ open, zoneId, title, onClose, onEditLink, refreshKey }: ManageLinksDialogProps) {
    const [links, setLinks] = React.useState<LinkedZoneDoc[]>([]);
    const [usedChannels, setUsedChannels] = React.useState<UsedLightChannel[]>([]);
    const [loading, setLoading] = React.useState(false);

    async function fetchData() {
        setLoading(true);
        const [linksData, channelsData] = await Promise.all([
            lediumApi.get<LinkedZoneDoc[]>(`linkedZones?sourceZoneIds=${zoneId}`),
            lediumApi.get<UsedLightChannel[]>(`zones/${zoneId}/lightChannels`)
        ]);
        setLinks((linksData ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)));
        setUsedChannels(channelsData ?? []);
        setLoading(false);
    }

    React.useEffect(() => {
        if (open) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, zoneId, refreshKey]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                {loading && (
                    <Box sx={{ py: 1 }}>
                        <Skeleton height={48} />
                        <Skeleton height={48} />
                    </Box>
                )}
                {!loading && links.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        {i18n('linked_project_none') as string}
                    </Typography>
                )}
                {!loading &&
                    links.map((link) => <LinkedProjectRow key={link._id} link={link} usedChannels={usedChannels} onRefresh={fetchData} onEdit={onEditLink} />)}
            </DialogContent>
        </Dialog>
    );
}

export default ManageLinksDialog;
